import {
  Autocomplete,
  CircularProgress,
  TextField,
  styled,
} from "@mui/material"
import { useEffect, useState } from "react"
import { getPaginationMeta, useGetCoursesQuery } from "../services/api"

export type CourseOption = { uuid: string; title: string }

export const CourseFilterSelect = styled(
  ({
    className,
    organizationId,
    value,
    onChange,
  }: {
    className?: string
    organizationId: string
    value: CourseOption[]
    onChange: (value: CourseOption[]) => void
  }) => {
    const [open, setOpen] = useState(false)
    const [input, setInput] = useState("")
    const [search, setSearch] = useState("")
    useEffect(() => {
      const timer = setTimeout(() => setSearch(input.trim()), 300)
      return () => clearTimeout(timer)
    }, [input])

    const {
      currentData: data,
      isFetching,
      isError,
    } = useGetCoursesQuery(
      { org_id: organizationId, search, limit: 50, offset: 0 },
      { skip: !open, refetchOnMountOrArgChange: true }
    )
    const loading = input.trim() !== search || isFetching
    const courses = (loading ? [] : data?.data || []).flatMap((course) =>
      course.uuid
        ? [{ uuid: course.uuid, title: course.title || course.uuid }]
        : []
    )
    const options = [
      ...value,
      ...courses.filter(
        (course) => !value.some((item) => item.uuid === course.uuid)
      ),
    ]
    const total = getPaginationMeta(data)?.total || 0

    return (
      <Autocomplete
        className={className}
        multiple
        size="small"
        sx={{ minWidth: 320 }}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        disableCloseOnSelect
        filterSelectedOptions
        limitTags={2}
        options={options}
        filterOptions={(items) => items}
        value={value}
        onChange={(_event, next, reason) => {
          onChange(next)
          if (reason === "selectOption") {
            setInput("")
            setSearch("")
          }
        }}
        inputValue={input}
        onInputChange={(_event, next, reason) => {
          if (reason === "input" || reason === "clear") setInput(next)
        }}
        loading={loading}
        loadingText="Searching courses…"
        noOptionsText={
          isError
            ? "Unable to search courses."
            : total > 0 && value.length > 0
            ? "All matching courses selected."
            : "No matching courses"
        }
        getOptionLabel={(course) => course.title}
        getOptionKey={(course) => course.uuid}
        isOptionEqualToValue={(option, selected) =>
          option.uuid === selected.uuid
        }
        getOptionDisabled={(option) =>
          value.length >= 25 && !value.some((item) => item.uuid === option.uuid)
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Courses"
            placeholder={value.length ? "Search courses" : "All courses"}
            error={isError}
            inputProps={{ ...params.inputProps, maxLength: 200 }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && open ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    )
  }
)(() => ({}))
